import { computed, ref } from 'vue'
import { getConfig, getConfigValue } from '@/api/config'

/** 配置中心：统一下单版本开关（legacy | v2-beta） */
export const ORDER_UNIFIED_VERSION_CONFIG = 'order.unified.version.switch'
export const ORDER_UNIFIED_VERSION_LEGACY = 'legacy'
export const ORDER_UNIFIED_VERSION_V2 = 'v2-beta'

const unifiedOrderVersionEnabled = ref(false)
const configLoaded = ref(false)
let pending: Promise<boolean> | null = null

function unwrapConfigValue(value: unknown): unknown {
  let current = value
  for (let depth = 0; depth < 6; depth += 1) {
    if (current === null || current === undefined) return current
    if (typeof current === 'string') {
      const text = current.trim()
      if (!text) return ''
      try {
        current = JSON.parse(text) as unknown
        continue
      } catch {
        return text
      }
    }
    if (typeof current !== 'object') return current
    const record = current as Record<string, unknown>
    if (record.content !== null && record.content !== undefined) {
      current = record.content
      continue
    }
    if (record.currentValue !== null && record.currentValue !== undefined) {
      current = record.currentValue
      continue
    }
    if (record.value !== null && record.value !== undefined) {
      current = record.value
      continue
    }
    if (record.configValue !== null && record.configValue !== undefined) {
      current = record.configValue
      continue
    }
    return current
  }
  return current
}

/** 配置值是否为新版统一下单（v2-beta） */
export function isUnifiedOrderVersionValue(value: unknown): boolean {
  const text = String(unwrapConfigValue(value) ?? '').trim().toLowerCase()
  if (!text || text === ORDER_UNIFIED_VERSION_LEGACY) return false
  if (text === ORDER_UNIFIED_VERSION_V2.toLowerCase()) return true
  if (text === 'v2-beat') return true
  return text.startsWith('v2')
}

async function fetchUnifiedOrderVersionRaw(): Promise<unknown> {
  try {
    const detail = await getConfig({ configName: ORDER_UNIFIED_VERSION_CONFIG })
    if (detail?.currentValue !== null && detail?.currentValue !== undefined && String(detail.currentValue).trim()) {
      return detail.currentValue
    }
    if (detail?.configValue !== null && detail?.configValue !== undefined && String(detail.configValue).trim()) {
      return detail.configValue
    }
  } catch (error) {
    console.warn('[CT_UNIFIED_ORDER] getConfig failed, fallback getConfigValue', error)
  }
  return getConfigValue(ORDER_UNIFIED_VERSION_CONFIG)
}

/**
 * 从配置中心加载统一下单版本（CCTOWER-2116）。
 * 默认 legacy；currentValue = v2-beta 时走新版。
 */
export async function loadUnifiedOrderVersionConfig(force = false): Promise<boolean> {
  if (configLoaded.value && !force) {
    return unifiedOrderVersionEnabled.value
  }
  if (pending && !force) {
    return pending
  }

  pending = fetchUnifiedOrderVersionRaw()
    .then(raw => {
      unifiedOrderVersionEnabled.value = isUnifiedOrderVersionValue(raw)
      configLoaded.value = true
      if (import.meta.env.DEV) {
        console.log('[CT_UNIFIED_ORDER]', { raw, enabled: unifiedOrderVersionEnabled.value })
      }
      return unifiedOrderVersionEnabled.value
    })
    .catch(error => {
      console.warn('[CT_UNIFIED_ORDER] load config failed, fallback to legacy', error)
      unifiedOrderVersionEnabled.value = false
      configLoaded.value = true
      return false
    })
    .finally(() => {
      pending = null
    })

  return pending
}

export function resetUnifiedOrderVersionConfigCache(): void {
  configLoaded.value = false
  unifiedOrderVersionEnabled.value = false
  pending = null
}

export function useUnifiedOrderVersion() {
  if (!configLoaded.value && !pending) {
    void loadUnifiedOrderVersionConfig()
  }

  const isUnifiedOrderVersion = computed(() =>
    configLoaded.value && unifiedOrderVersionEnabled.value
  )

  return {
    isUnifiedOrderVersion,
    configLoaded,
    loadUnifiedOrderVersionConfig
  }
}

export function useUnifiedOrderVersionState() {
  return {
    isUnifiedOrderVersionEnabled: unifiedOrderVersionEnabled,
    isUnifiedOrderVersionConfigLoaded: configLoaded
  }
}
